import { useEffect, useRef, useState } from "react";
import { X, UploadCloud } from "lucide-react";
import { uploadGalleryImageSupabase } from "../lib/supabase";

export default function GalleryUploadModal({ existingCategories = [], onClose, onDone }) {
  const [files, setFiles] = useState([]);
  const [category, setCategory] = useState(existingCategories[0] || "");
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [err, setErr] = useState("");
  const inputRef = useRef(null);

  const pick = (e) => {
    const list = Array.from(e.target.files || []);
    setFiles(list);
  };

  const upload = async () => {
    setErr("");
    if (!category.trim()) { setErr("Please enter a category."); return; }
    if (files.length === 0) { setErr("Please choose at least one image."); return; }
    setBusy(true);
    setProgress({ done: 0, total: files.length });
    try {
      for (let i = 0; i < files.length; i++) {
        await uploadGalleryImageSupabase(files[i], category.trim(), caption.trim());
        setProgress({ done: i + 1, total: files.length });
      }
      onDone();
    } catch (e) {
      setErr(e.message || e.response?.data?.detail || "Upload failed.");
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay" onMouseDown={busy ? undefined : onClose}>
      <div className="modal" onMouseDown={(e) => e.stopPropagation()} data-testid="gallery-upload-modal">
        <div className="modal-head">
          <h3>Upload Photos</h3>
          <button className="icon-btn" onClick={onClose} disabled={busy}><X /></button>
        </div>
        <div className="modal-body">
          {err && <div className="login-error">{err}</div>}
          <div
            className="upload-drop"
            onClick={() => inputRef.current?.click()}
            data-testid="upload-drop"
          >
            <UploadCloud />
            <div>
              <strong>Click to select images</strong>
              <span>JPG / PNG / WEBP · up to 8MB each · multiple allowed</span>
            </div>
            <input ref={inputRef} type="file" accept="image/*" multiple hidden onChange={pick} data-testid="upload-input" />
          </div>
          {files.length > 0 && (
            <div className="upload-files" data-testid="upload-file-count">{files.length} file(s) selected</div>
          )}
          <div className="field">
            <label>Category / Project</label>
            <input
              list="gal-cats"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="e.g. EMMVEE Energy"
              data-testid="upload-category"
            />
            <datalist id="gal-cats">
              {existingCategories.map((c) => <option key={c} value={c} />)}
            </datalist>
          </div>
          <div className="field">
            <label>Caption <span style={{ color: "var(--muted)", fontWeight: 500 }}>(optional, applied to all)</span></label>
            <input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="e.g. LT panel installation" data-testid="upload-caption" />
          </div>
          {busy && (
            <div className="upload-progress">
              <div className="bar"><span style={{ width: `${(progress.done / progress.total) * 100}%` }} /></div>
              <span>Uploading {progress.done} / {progress.total}…</span>
            </div>
          )}
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost btn-sm" style={{ color: "var(--ink)", borderColor: "var(--line)", background: "#fff" }} onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={upload} disabled={busy} data-testid="upload-submit">
            {busy ? "Uploading…" : `Upload ${files.length || ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}
